// src/components/homepage/resources/ResourceBackdrop.client.tsx

"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { motion, useInView, useReducedMotion } from "motion/react"
import { type ResourceScreenshot } from "~/components/homepage/resources/ResourceData"


/**
 * Props for the ResourceBackdrop component
 */
interface ResourceBackdropProps {
  /** Array of screenshots to display in the slideshow */
  screenshots: ResourceScreenshot[]
  /** Time in milliseconds between slide transitions */
  rotateMs?: number
}

/**
 * A backdrop component that displays a rotating slideshow of resource screenshots
 * with smooth transitions and accessibility considerations.
 * 
 * Features:
 * - Automatic rotation with configurable timing
 * - Respects user's reduced motion preferences
 * - Pauses when component is out of view or page is hidden
 * - Smooth crossfade transitions between images
 * - Category indicator synchronized with current slide
 */
export default function ResourceBackdrop({
  screenshots,
  rotateMs = 4000,
}: ResourceBackdropProps): JSX.Element | null {
  const prefersReduced = useReducedMotion()
  const ref = useRef<HTMLDivElement | null>(null)
  const inView = useInView(ref, { margin: "-100px", once: false })

  const [currentIndex, setCurrentIndex] = useState(0)
  const screenshotCount = screenshots.length

  // Clamp index if the list length changes (prevents undefined lookups)
  useEffect(() => {
    if (screenshotCount === 0) return
    if (currentIndex >= screenshotCount) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional client-side state update inside an effect (SSR-safe staging of derived UI state)
      setCurrentIndex(0)
    }
  }, [screenshotCount, currentIndex])

  // Auto-rotation effect with visibility and reduced motion checks
  useEffect(() => {
    if (prefersReduced || !inView || screenshotCount === 0) return

    let isVisible = document.visibilityState === "visible"
    
    const handleVisibilityChange = (): void => {
      isVisible = document.visibilityState === "visible"
    }
    
    document.addEventListener("visibilitychange", handleVisibilityChange)

    const intervalId = window.setInterval(() => {
      if (!isVisible) return
      setCurrentIndex((prevIndex) => (prevIndex + 1) % screenshotCount)
    }, rotateMs)

    return (): void => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      clearInterval(intervalId)
    }
  }, [prefersReduced, inView, screenshotCount, rotateMs])

  // Early return if no screenshots available
  if (screenshotCount === 0) return null

  const previousIndex = (currentIndex - 1 + screenshotCount) % screenshotCount

  return (
    <div
      ref={ref}
      className="absolute inset-0 pointer-events-none"
      aria-hidden
    >
      {/* Slides (previous + current) for smooth crossfade */}
      {[previousIndex, currentIndex].map((slideIndex) => {
        const screenshot = screenshots[slideIndex]
        const isActive = slideIndex === currentIndex
        
        // Type guard to ensure screenshot exists
        if (!screenshot) return null
        
        return (
          <motion.div
            key={`${screenshot.src}-${slideIndex}`}
            className="absolute inset-0"
            style={{ willChange: "opacity" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: isActive ? 1 : 0 }}
            transition={{ 
              duration: prefersReduced ? 0 : 1, 
              ease: "easeInOut" as const 
            }}
          >
            <Image
              src={screenshot.src}
              alt=""
              aria-hidden
              fill
              className="object-cover object-center select-none"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
              loading="lazy"
              quality={50}
              draggable={false}
            />
          </motion.div>
        )
      })}

      {/* Single overlay to prevent double-stacking during crossfade */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 dark:from-black/90 dark:via-black/70 dark:to-black/50" />

      {/* Category indicator synchronized with the current slide */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
          <div className="w-2 h-2 bg-blue-400 rounded-full" />
          <span className="text-sm text-gray-200 capitalize" aria-live="polite">
            {screenshots[currentIndex]?.category ?? "Unknown"}
          </span>
        </div>
      </div>
    </div>
  )
}
